import { validateExclusions } from './cropExclusions';

// Publication validation is separate from the frozen legacy adapter in contract.js.
export const validId = (value) => typeof value === 'string' && value.length > 0 && value.length <= 200;
export const finite = (value) => typeof value === 'number' && Number.isFinite(value);
export const isMapPreview = (snapshot) => snapshot?.publication_mode === 'map_preview' && snapshot.statistics_availability === 'UNAVAILABLE';
const check = (value) => { if (!value) throw new Error('Invalid Crop Intelligence publication response'); };
const close = (a, b) => Math.abs(a - b) <= Math.max(0.00001, Math.abs(b) * 0.000001);
const percent = (value) => value === null || (finite(value) && value >= 0 && value <= 100.000001);
export function coverage(data) {
  check(data && ['COMPLETE', 'PARTIAL', 'UNAVAILABLE'].includes(data.status));
  check(Number.isInteger(data.covered_unit_count) && data.covered_unit_count >= 0);
  check(data.expected_unit_count === null || (Number.isInteger(data.expected_unit_count) && data.expected_unit_count >= data.covered_unit_count));
  check(percent(data.coverage_percentage) && typeof data.summary === 'string');
  return data;
}
export function publication(data) {
  check(data && ['snapshot_id', 'release_id', 'class_schema_id', 'boundary_revision_id', 'statistics_algorithm_version', 'style_revision'].every((key) => validId(data[key])));
  check(/^[A-Z]{2}$/.test(data.country_code) && Number.isInteger(data.year) && typeof data.title === 'string');
  check(data.publication_status === 'ACTIVE' && data.build_status === 'READY');
  check(Array.isArray(data.classes) && data.classes.length > 0 && data.classes.length <= 1000);
  const codes = new Set();
  data.classes.forEach((entry) => {
    check(Number.isInteger(entry.code) && !codes.has(entry.code) && validId(entry.semantic_key) && validId(entry.name));
    check(['fill', 'stroke'].every((key) => /^#[a-f0-9]{6}$/i.test(entry[key])));
    check(['is_crop', 'include_in_agricultural_area', 'include_in_classified_area'].every((key) => typeof entry[key] === 'boolean'));
    check(!entry.include_in_agricultural_area || entry.include_in_classified_area);
    codes.add(entry.code);
  });
  check(Array.isArray(data.present_class_codes) && new Set(data.present_class_codes).size === data.present_class_codes.length && data.present_class_codes.every((code) => codes.has(code)));
  check(finite(data.rendering?.fill_opacity) && data.rendering.fill_opacity >= 0 && data.rendering.fill_opacity <= 1);
  check(finite(data.rendering?.stroke_width) && data.rendering.stroke_width >= 0 && data.rendering.stroke_width <= 20);
  coverage(data.coverage);
  return data;
}
export function catalog(data, country) {
  check(data?.country_code === country && Array.isArray(data.publications));
  const seen = new Set();
  data.publications.forEach((item) => {
    publication(item);
    check(item.country_code === country && !seen.has(item.snapshot_id));
    seen.add(item.snapshot_id);
  });
  check(data.default_snapshot_id === null || seen.has(data.default_snapshot_id));
  return data;
}
export function scopeIdentity(actual, expected, accessRevision, data) {
  check(actual && actual.kind === expected.kind);
  if (expected.subject_id) check(actual.subject_id === expected.subject_id);
  if (expected.membership_revision != null) check(String(actual.membership_revision) === String(expected.membership_revision));
  check(data.access_revision === accessRevision);
}
export function statistics(data, snapshot, scope, accessRevision, excludedClassKeys = []) {
  validateExclusions(data, excludedClassKeys);
  check(data && ['snapshot_id', 'release_id', 'country_code', 'year', 'class_schema_id', 'boundary_revision_id', 'statistics_algorithm_version'].every((key) => data[key] === snapshot[key]));
  if (scope) scopeIdentity(data.scope, scope, accessRevision, data);
  coverage(data.coverage);
  check(Array.isArray(data.classes) && Array.isArray(data.present_class_codes));
  if (excludedClassKeys.length) {
    const presence = data.unfiltered_present_class_codes;
    check(Array.isArray(presence) && new Set(presence).size === presence.length && presence.every((code) => snapshot.classes.some((entry) => entry.code === code)));
    check(data.present_class_codes.every((code) => presence.includes(code)));
    check(data.classes.every((row) => !excludedClassKeys.includes(snapshot.classes.find((entry) => entry.code === row.code)?.semantic_key)));
  }
  if (data.coverage.status === 'UNAVAILABLE') {
    check(data.classified_area_ha === null && data.agricultural_area_ha === null && !data.classes.length && !data.present_class_codes.length);
    return data;
  }
  check(finite(data.classified_area_ha) && data.classified_area_ha >= 0 && finite(data.agricultural_area_ha) && data.agricultural_area_ha >= 0 && data.agricultural_area_ha <= data.classified_area_ha);
  let classified = 0; let agricultural = 0;
  const seen = new Set();
  data.classes.forEach((row) => {
    const entry = snapshot.classes.find((item) => item.code === row.code);
    check(entry && !seen.has(row.code) && finite(row.area_ha) && row.area_ha >= 0);
    seen.add(row.code);
    if (entry.include_in_classified_area) classified += row.area_ha;
    if (entry.include_in_agricultural_area) agricultural += row.area_ha;
    ['classified', 'agricultural'].forEach((basis) => {
      const value = row[`share_of_${basis}_area_pct`];
      check(percent(value));
      const denominator = data[`${basis}_area_ha`];
      if (excludedClassKeys.length && denominator === 0) check(value === null);
      if (entry[`include_in_${basis}_area`] && denominator > 0) check(value !== null && close(value, 100 * row.area_ha / denominator));
    });
  });
  check(close(classified, data.classified_area_ha) && close(agricultural, data.agricultural_area_ha));
  check(new Set(data.present_class_codes).size === data.present_class_codes.length && data.present_class_codes.every((code) => seen.has(code)));
  return data;
}
export function comparison(data, snapshots, scope, accessRevision, excludedClassKeys = []) {
  validateExclusions(data, excludedClassKeys);
  check(data && Array.isArray(data.snapshot_ids) && JSON.stringify(data.snapshot_ids) === JSON.stringify(snapshots.map((s) => s.snapshot_id)));
  scopeIdentity(data.scope, scope, accessRevision, data);
  check(typeof data.compatible === 'boolean' && Array.isArray(data.results) && data.results.length === snapshots.length && Array.isArray(data.deltas));
  data.results.forEach((item, index) => statistics(item, snapshots[index], undefined, undefined, excludedClassKeys));
  if (excludedClassKeys.length) {
    check(data.deltas.every((row) => !snapshots.some((s) => s.classes.some((entry) => entry.code === row.code && excludedClassKeys.includes(entry.semantic_key)))));
    check(data.rankings?.class_code == null || !snapshots.some((s) => s.classes.some((entry) => entry.code === data.rankings.class_code && excludedClassKeys.includes(entry.semantic_key))));
  }
  check(data.compatible || !data.deltas.length);
  data.deltas.forEach((row) => check(Number.isInteger(row.code) && finite(row.delta_ha) && (row.delta_pct === null || finite(row.delta_pct)) && Number.isInteger(row.to_snapshot_index) && row.to_snapshot_index > 0 && row.to_snapshot_index < snapshots.length));
  check(Array.isArray(data.rankings?.items));
  // pg returns numeric ranking columns as strings; normalize only these documented fields.
  return { ...data, rankings: { ...data.rankings, items: data.rankings.items.map((row) => {
    check(validId(row.admin_id));
    const result = { ...row };
    ['baseline_area_ha', 'area_ha', 'delta_ha', 'delta_pct'].forEach((key) => {
      check(row[key] === null || finite(row[key]) || (typeof row[key] === 'string' && row[key].trim() !== '' && finite(Number(row[key]))));
      result[key] = row[key] === null ? null : Number(row[key]);
    });
    return result;
  }) } };
}
export function cropLabel(entry, t, language) {
  if (!entry) return t('ci.unknownClass');
  return entry.translations?.[language] || entry.translations?.[language?.split('-')[0]] ||
    (entry.label_key ? t(entry.label_key, { defaultValue: entry.name }) : entry.name);
}
export function cropStyle(snapshot, feature) {
  const entry = snapshot.classes.find((row) => row.code === feature.properties?.crop_code);
  return { stroke: snapshot.rendering.stroke_width > 0, color: entry?.stroke || '#555555', fillColor: entry?.fill || '#FF00FF',
    fillOpacity: snapshot.rendering.fill_opacity, weight: snapshot.rendering.stroke_width };
}
